import {
  type QuestProgress,
  type QuestReward,
  type QuestTypeValue,
  STARTER_QUESTS,
} from '@dreamgame/shared';
import { signal } from '@preact/signals';

// ============================================================================
// Quest State - Starter Quests Progress
// ============================================================================

// Map of quest progress by quest type
export const questProgress = signal<Map<QuestTypeValue, QuestProgress>>(new Map());

// ============================================================================
// Actions
// ============================================================================

// Initialize quests from server sync
export const setQuests = ({ quests }: { quests: ReadonlyArray<QuestProgress> }): void => {
  const newMap = new Map<QuestTypeValue, QuestProgress>();
  for (const quest of quests) {
    newMap.set(quest.type, quest);
  }
  questProgress.value = newMap;
};

// Update a single quest progress
export const updateQuest = ({ quest }: { quest: QuestProgress }): void => {
  const newMap = new Map(questProgress.value);
  newMap.set(quest.type, quest);
  questProgress.value = newMap;
};

// ============================================================================
// Helpers
// ============================================================================

// Get all quests with their definitions for display (only server-sent quests)
export const getQuestsForDisplay = (): ReadonlyArray<{
  type: QuestTypeValue;
  name: string;
  description: string;
  icon: string;
  currentAmount: number;
  targetAmount: number;
  completed: boolean;
}> => {
  const result: Array<{
    type: QuestTypeValue;
    name: string;
    description: string;
    icon: string;
    currentAmount: number;
    targetAmount: number;
    completed: boolean;
  }> = [];

  // Iterate in STARTER_QUESTS order but only include quests the server sent
  for (const def of STARTER_QUESTS) {
    const progress = questProgress.value.get(def.type);
    if (!progress) {
      continue;
    }
    result.push({
      type: def.type,
      name: def.name,
      description: def.description,
      icon: def.icon,
      currentAmount: progress.currentAmount,
      targetAmount: def.targetAmount,
      completed: progress.completed,
    });
  }

  return result;
};

// Get completion count
export const getCompletedCount = (): number => {
  let count = 0;
  for (const quest of questProgress.value.values()) {
    if (quest.completed) {
      count++;
    }
  }
  return count;
};

// Check if all quests are complete
export const areAllQuestsComplete = (): boolean => {
  return getCompletedCount() === STARTER_QUESTS.length;
};

// ============================================================================
// Quest Chain Display
// ============================================================================

export type QuestDisplayStatus = 'completed' | 'active' | 'locked';

export interface QuestChainItem {
  readonly type: QuestTypeValue;
  readonly name: string;
  readonly description: string;
  readonly icon: string;
  readonly status: QuestDisplayStatus;
  readonly currentAmount: number;
  readonly targetAmount: number;
  readonly rewards: ReadonlyArray<QuestReward>;
}

const computeStatus = ({
  progress,
}: {
  progress: QuestProgress | undefined;
}): QuestDisplayStatus => {
  if (!progress) {
    return 'locked';
  }
  return progress.completed ? 'completed' : 'active';
};

const STATUS_ORDER: Record<QuestDisplayStatus, number> = {
  completed: 0,
  active: 1,
  locked: 2,
};

// Get all quests with computed display status (including locked ones)
// Sorted: completed first (in definition order), then active, then locked
export const getQuestChainDisplay = (): ReadonlyArray<QuestChainItem> => {
  const progressMap = questProgress.value;
  const result: QuestChainItem[] = [];

  for (const def of STARTER_QUESTS) {
    const progress = progressMap.get(def.type);
    result.push({
      type: def.type,
      name: def.name,
      description: def.description,
      icon: def.icon,
      status: computeStatus({ progress }),
      currentAmount: progress?.currentAmount ?? 0,
      targetAmount: def.targetAmount,
      rewards: def.rewards,
    });
  }

  result.sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]);

  return result;
};

// Get chain completion progress
export const getChainProgress = (): { completed: number; total: number } => ({
  completed: getCompletedCount(),
  total: STARTER_QUESTS.length,
});
